/** @format */
import {
    Icon,
    IconSize,
    IconVariant,
} from '@mpb-com/feather/components/atoms/icon';
import { createClassNames } from '@mpb-com/feather/helpers/styles';
import { Trans, useTranslation } from 'next-i18next';
import React, { FunctionComponent, useContext, useMemo } from 'react';
import styled from 'styled-components';

import TickIcon from '@toucan/client/global/icons/checkmark.svg';

import { MarketContext } from '@toucan/client/global/market/context';

import { getFilterOption } from '@toucan/client/features/search/components/product-search-results/components/accessory-filters/helpers';
import { getModelIdFromQuery } from '@toucan/client/features/search/helpers';
import { usePriceModifierGroups } from '@toucan/client/features/search/hooks/use-model-price-modifier-groups';

import { AccessoryOption } from '../accessory-option';
import { TEST_ID as dataTestId } from './constants';
import { filterModifierGroupsByAvailableOptions } from './helpers';
import { styles } from './included-accessory-filter.styles';
import { IncludedAccessoryFilterProps } from './types';

const StyledAccessoryFilter = styled('div')`
    ${styles}
`;

export const IncludedAccessoryFilter: FunctionComponent<
    IncludedAccessoryFilterProps
> = ({
    filter,
    onAccessorySelect,
    showIcon = true,
    isGroupNameFullWidthStyle = false,
}) => {
    const { t } = useTranslation();
    const { activeMarket } = useContext(MarketContext);

    const { selectedOptions } = filter;

    const modelId = getModelIdFromQuery(filter.baseFilterQuery);

    const { data: modifierGroups } = usePriceModifierGroups(
        modelId,
        activeMarket.code
    );
    const essentialModifierGroups = modifierGroups?.essential;
    const availableOptions = filter.options.map(({ value }) => Number(value));

    const includedAccessories = useMemo(
        () =>
            filterModifierGroupsByAvailableOptions(
                essentialModifierGroups,
                availableOptions
            ),
        [essentialModifierGroups, availableOptions]
    );

    const renderedFilteredOptions = useMemo(() => {
        return includedAccessories.map((group) => {
            const options = group.modifiers.map(({ id }) => (
                <AccessoryOption
                    key={id}
                    option={getFilterOption(id, filter)}
                    selectedOptions={selectedOptions}
                    onAccessorySelect={onAccessorySelect}
                />
            ));

            return (
                <div
                    key={group.id}
                    className="included-accessory__options"
                    data-testid={`included-accessory__options--${group.id}`}
                >
                    <span
                        className={createClassNames(
                            'included-accessory__group-name',
                            {
                                'included-accessory__group-name--full-width':
                                    isGroupNameFullWidthStyle,
                            }
                        )}
                    >
                        {group.name}
                    </span>

                    {options}
                </div>
            );
        });
    }, [
        filter,
        onAccessorySelect,
        selectedOptions,
        includedAccessories,
        isGroupNameFullWidthStyle,
    ]);

    return (
        <StyledAccessoryFilter data-testid={dataTestId}>
            <div className="included-accessory__title">
                <Icon
                    variant={IconVariant.CORE}
                    size={IconSize.XL}
                    data-testid={`${dataTestId}__icon`}
                    className={createClassNames('included-accessory__icon', {
                        'included-accessory__icon--hidden': !showIcon,
                    })}
                >
                    <TickIcon />
                </Icon>
                <p className="included-accessory__label">
                    {t('Included accessories')}
                </p>
            </div>
            <p className="included-accessory__description">
                <Trans components={{ ['bold-text']: <strong /> }}>
                    {t(
                        'Every item comes with the <bold-text>essentials included</bold-text>. You can choose between original or third-party accessories.'
                    )}
                </Trans>
            </p>
            {renderedFilteredOptions}
        </StyledAccessoryFilter>
    );
};
